import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { startGoogleSignIn } from "@/components/auth/GoogleSignInButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { user, refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [product, setProduct] = useState("");
  const polls = useRef(0);
  const latest = useRef({ user, refresh });
  latest.current = { user, refresh };

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        setProduct(data.product_name || "");
        if (data.payment_status === "paid") {
          setState("paid");
          if (latest.current.user) latest.current.refresh();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
        polls.current += 1;
        if (polls.current >= MAX_POLLS) { setState("pending"); return; }
        timer = setTimeout(poll, 2000);
      } catch {
        setState("error");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <main className="min-h-screen flex items-center justify-center px-6" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.65, 0, 0.35, 1] }}
        className="max-w-lg w-full text-center"
      >
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto text-[#B45309] animate-spin mb-8" size={48} strokeWidth={1.2} />
            <h1 className="font-display text-4xl md:text-5xl tracking-tight mb-4">Confirming your payment…</h1>
            <p className="text-[#78716C]">One moment while we verify with Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-[#B45309] mb-8" size={56} strokeWidth={1.2} />
            <div className="section-tag mb-4">Payment confirmed</div>
            <h1 className="font-display text-4xl md:text-6xl tracking-tight mb-4">You are in.</h1>
            <p className="text-[#78716C] mb-10">
              {product ? `"${product}" is now unlocked` : "Your purchase is unlocked"}
              {user
                ? " in your Member Dashboard."
                : ". Create an account using the SAME email you paid with, and it unlocks automatically in your dashboard."}
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              {user
                ? <Link to="/dashboard" className="btn-ember text-sm" data-testid="success-dashboard">Go to Dashboard <ArrowUpRight size={16} /></Link>
                : (
                  <>
                    <button onClick={startGoogleSignIn} className="btn-ember text-sm" data-testid="success-google">
                      <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.6 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
                        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                        <path fill="#4CAF50" d="M24 44c5.2 0 9.8-2 13.3-5.2l-6.2-5.2C29.1 34.7 26.7 36 24 36c-5.3 0-9.7-3.4-11.3-8.1l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.6l6.2 5.2C40.9 35 44 30 44 24c0-1.2-.1-2.4-.4-3.5z"/>
                      </svg>
                      Continue with Google
                    </button>
                    <Link to="/register" className="btn-outline text-sm" data-testid="success-register">Use email instead <ArrowUpRight size={16} /></Link>
                  </>
                )}
            </div>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="mx-auto text-[#B45309] mb-8" size={48} strokeWidth={1.2} />
            <h1 className="font-display text-4xl tracking-tight mb-4">Still processing…</h1>
            <p className="text-[#78716C] mb-8">Your payment is taking a moment. It will appear in your dashboard shortly.</p>
            <Link to={user ? "/dashboard" : "/"} className="btn-outline text-sm">{user ? "Go to Dashboard" : "Back home"}</Link>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="mx-auto text-red-400 mb-8" size={48} strokeWidth={1.2} />
            <h1 className="font-display text-4xl tracking-tight mb-4">We could not confirm this.</h1>
            <p className="text-[#78716C] mb-8">If you were charged, it will still unlock automatically. Contact us if anything looks off.</p>
            <Link to="/" className="btn-outline text-sm">Back home</Link>
          </>
        )}
      </motion.div>
    </main>
  );
}
